import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaCheckCircle, FaTelegramPlane } from 'react-icons/fa';
import SystemNotification from '@/app/components/feedback/SystemNotification';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { sendFeedbackToChatwoot } from './core/_requests';
import type { FeedbackType } from './core/_models';

type FeedbackModalProps = {
  show: boolean;
  /** Pathname the user was on when they opened the modal — sent as feedback context. */
  page?: string;
  onClose: () => void;
};

const FeedbackModal = ({ show, page, onClose }: FeedbackModalProps) => {
  const { currentUser } = useAuth();
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState(currentUser?.email ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const resetForm = () => {
    setFeedbackType('');
    setFeedbackMessage('');
    setFeedbackEmail(currentUser?.email ?? '');
    setIsSubmitting(false);
    setIsSuccess(false);
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedbackType || !feedbackMessage.trim()) {
      SystemNotification.warning('Selecione um tipo e descreva seu feedback.');
      return;
    }

    setIsSubmitting(true);
    try {
      await sendFeedbackToChatwoot(
        {
          type: feedbackType as FeedbackType,
          message: feedbackMessage,
          email: feedbackEmail,
        },
        {
          id: currentUser?.id,
          name: currentUser?.name,
          email: currentUser?.email,
          phone: currentUser?.telephone,
          role: currentUser?.role,
          environmentId: currentUser?.environment_id,
        },
        { page },
      );
      setIsSuccess(true);
    } catch {
      SystemNotification.error('Não foi possível enviar seu feedback. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal centered show={show} onExited={resetForm} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Compartilhe sua opinião</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isSuccess ? (
          <div className="text-center py-8 animate__animated animate__fadeIn">
            <FaCheckCircle
              className="text-success animate__animated animate__bounceIn mb-6"
              style={{ fontSize: '5rem' }}
            />
            <h3 className="fw-bold text-gray-900 fs-2 mb-3">Feedback enviado!</h3>
            <p className="text-gray-600 fs-6 mb-8 mw-400px mx-auto">
              Obrigado por compartilhar sua opinião. Sua mensagem foi recebida pela nossa equipe.
            </p>
            <div className="d-flex justify-content-center gap-3">
              <button className="btn btn-light" type="button" onClick={onClose}>
                Fechar
              </button>
              <button className="btn btn-light-primary" type="button" onClick={() => setIsSuccess(false)}>
                <FaTelegramPlane className="me-2" />
                Enviar outro feedback
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-gray-600 fs-6 mb-6">
              Sua opinião é muito importante para nós. Compartilhe sugestões de melhorias para o sistema.
            </p>
            <form onSubmit={handleSubmitFeedback}>
              <div className="mb-5">
                <label className="form-label fs-6 fw-bold text-gray-700">Tipo de Feedback</label>
                <select
                  className="form-select"
                  disabled={isSubmitting}
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="sugestao">Sugestão</option>
                  <option value="problema">Problema</option>
                  <option value="elogio">Elogio</option>
                  <option value="duvida">Dúvida</option>
                </select>
              </div>

              <div className="mb-5">
                <label className="form-label fs-6 fw-bold text-gray-700">Sua Sugestão</label>
                <textarea
                  className="form-control"
                  disabled={isSubmitting}
                  placeholder="Descreva sua sugestão de melhoria ou problema encontrado..."
                  rows={4}
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                />
              </div>

              <div className="mb-5">
                <label className="form-label fs-6 fw-bold text-gray-700">Email para Resposta (opcional)</label>
                <input
                  className="form-control"
                  disabled={isSubmitting}
                  placeholder="seu@email.com"
                  type="email"
                  value={feedbackEmail}
                  onChange={(e) => setFeedbackEmail(e.target.value)}
                />
              </div>

              <button className="btn btn-primary w-100" disabled={isSubmitting} type="submit">
                {isSubmitting ? (
                  <span className="indicator-progress d-block">
                    Enviando...
                    <span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                ) : (
                  <>
                    <FaTelegramPlane className="me-2" />
                    Enviar Feedback
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default FeedbackModal;
